import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Stamp, Loader2, RefreshCw, Type, Image as ImageIcon, Upload } from "lucide-react";
import { api, API, errText, photoUrl } from "../../lib/api";

const SITE_LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_critter-classifieds/artifacts/5d445b3d93981940_logo%20watermark.png";

const POS = [["tl", "Top left"], ["tr", "Top right"], ["center", "Centre"], ["bl", "Bottom left"], ["br", "Bottom right"]];
const DOT = { tl: "top-1.5 left-1.5", tr: "top-1.5 right-1.5", center: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2", bl: "bottom-1.5 left-1.5", br: "bottom-1.5 right-1.5" };

const PositionPicker = ({ value, onChange }) => (
  <div data-testid="wm-position">
    <span className="text-sm font-semibold text-[#12234A]">Position</span>
    <div className="mt-1 grid grid-cols-5 gap-2">
      {POS.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} title={label} aria-label={label} data-testid={`wm-pos-${k}`}
          className={`group flex flex-col items-center gap-1 rounded-xl border-2 p-2 transition-colors ${value === k ? "border-[#F5551E] bg-[#FFF4F0]" : "border-slate-200 hover:border-slate-300"}`}>
          <span className="relative block h-9 w-full rounded-md bg-slate-100">
            <span className={`absolute h-2 w-5 rounded-sm ${DOT[k]} ${value === k ? "bg-[#F5551E]" : "bg-slate-400"}`} />
          </span>
          <span className={`text-[10px] font-semibold ${value === k ? "text-[#F5551E]" : "text-slate-500"}`}>{label}</span>
        </button>
      ))}
    </div>
  </div>
);

const LogoPicker = ({ path, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.png$/i.test(file.name)) return toast.error("Please choose a PNG file (transparent background works best)");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onChange(data.path); toast.success("Logo uploaded. Check the preview, then save.");
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="wm-logo-picker">
      <span className="text-sm font-semibold text-[#12234A]">Logo</span>
      <div className="mt-1 grid grid-cols-2 gap-2">
        <button type="button" onClick={() => onChange("")} data-testid="wm-logo-site"
          className={`rounded-xl border-2 p-3 h-20 flex items-center justify-center transition-colors ${!path ? "border-[#F5551E] bg-[#FFF4F0]" : "border-slate-200 hover:border-slate-300"}`}>
          <img src={SITE_LOGO} alt="Site logo" className="max-h-10 object-contain" />
        </button>
        <label data-testid="wm-logo-custom" className={`rounded-xl border-2 p-3 h-20 flex items-center justify-center cursor-pointer transition-colors ${path ? "border-[#F5551E] bg-[#FFF4F0]" : "border-dashed border-slate-300 hover:border-slate-400"}`}>
          <input type="file" accept="image/png" className="hidden" onChange={upload} data-testid="wm-logo-upload" />
          {busy ? <Loader2 size={18} className="animate-spin text-slate-400" /> : path
            ? <img src={photoUrl(path)} alt="Custom logo" className="max-h-12 object-contain" />
            : <span className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1.5"><Upload size={14} /> Upload custom PNG</span>}
        </label>
      </div>
      <p className="text-xs text-slate-400 mt-1.5">{path ? "Custom logo selected. Click the site logo to switch back." : "Using the CritterPosts.ca site logo."}</p>
    </div>
  );
};
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Slider } from "../../components/ui/slider";

const Progress = ({ p }) => {
  if (!p) return null;
  const pct = p.photos ? Math.round((p.up_to_date / p.photos) * 100) : 100;
  return (
    <div className="mt-4" data-testid="wm-progress">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="flex items-center gap-1.5">{p.running && <Loader2 size={12} className="animate-spin" />}{p.up_to_date} / {p.photos} listing photos up to date</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden"><div className="h-full bg-[#F5551E] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

export function WatermarkSettings() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState("");
  const load = useCallback(() => api.get("/admin/site/watermark").then((r) => setF((x) => (x ? { ...x, progress: r.data.progress } : r.data))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!f?.progress?.running && f?.progress?.up_to_date === f?.progress?.photos) return undefined;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [f?.progress, load]);
  useEffect(() => {
    if (!f) return undefined;
    const h = setTimeout(() => setPreviewKey(new URLSearchParams({ text: f.text || "critterposts.ca", size_pct: f.size_pct, opacity: f.opacity, mode: f.mode, logo_path: f.mode === "logo" ? f.logo_path || "" : "", position: f.position || "br" }).toString()), 350);
    return () => clearTimeout(h);
  }, [f?.text, f?.size_pct, f?.opacity, f?.mode, f?.logo_path, f?.position]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin inline" /></div>;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site/watermark", { enabled: f.enabled, mode: f.mode, logo_path: f.logo_path || "", text: f.text || "critterposts.ca", size_pct: f.size_pct, opacity: f.opacity, position: f.position || "br" });
      setF(data);
      toast.success(f.enabled ? "Watermark saved. Updating all listing photos…" : "Watermark turned off. Restoring clean photos…");
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="watermark-settings">
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
        <h2 className="font-display font-bold text-[#12234A] text-lg flex items-center gap-2"><Stamp size={18} className="text-[#F5551E]" /> Photo watermark</h2>
        <p className="text-sm text-slate-500 mt-1">Stamped into every listing photo at the position you choose. Changes are applied to all photos, old and new, from the saved originals. Turning it off restores clean photos.</p>
        <div className="mt-5 flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
          <span className="font-semibold text-[#12234A] text-sm">Watermark on</span>
          <Switch checked={f.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="wm-enabled" />
        </div>
        <div className={`space-y-5 mt-5 ${f.enabled ? "" : "opacity-50 pointer-events-none"}`}>
          <div>
            <span className="text-sm font-semibold text-[#12234A]">Style</span>
            <div className="mt-1 grid grid-cols-2 gap-2" data-testid="wm-mode">
              {[["text", Type, "Text"], ["logo", ImageIcon, "Logo"]].map(([m, Icon, label]) => (
                <button key={m} type="button" onClick={() => set("mode", m)} data-testid={`wm-mode-${m}`}
                  className={`h-11 rounded-xl border-2 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${f.mode === m ? "border-[#F5551E] bg-[#FFF4F0] text-[#F5551E]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                  <Icon size={15} /> {label}
                </button>
              ))}
            </div>
          </div>
          {f.mode === "logo" ? <LogoPicker path={f.logo_path} onChange={(p) => set("logo_path", p)} /> : (
          <div>
            <label className="text-sm font-semibold text-[#12234A]" htmlFor="wm-text">Text</label>
            <Input id="wm-text" value={f.text} maxLength={40} onChange={(e) => set("text", e.target.value)} data-testid="wm-text" className="mt-1 h-11 rounded-xl" />
          </div>
          )}
          <PositionPicker value={f.position || "br"} onChange={(v) => set("position", v)} />
          <div>
            <div className="flex justify-between text-sm"><span className="font-semibold text-[#12234A]">Size</span><span className="text-slate-500" data-testid="wm-size-value">{Number(f.size_pct).toFixed(1)}% of photo</span></div>
            <Slider value={[f.size_pct]} min={2} max={10} step={0.5} onValueChange={([v]) => set("size_pct", v)} className="mt-3" data-testid="wm-size" />
          </div>
          <div>
            <div className="flex justify-between text-sm"><span className="font-semibold text-[#12234A]">Visibility</span><span className="text-slate-500" data-testid="wm-opacity-value">{f.opacity}%</span></div>
            <Slider value={[f.opacity]} min={20} max={100} step={5} onValueChange={([v]) => set("opacity", v)} className="mt-3" data-testid="wm-opacity" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-6">
          <Button onClick={save} disabled={saving || (f.mode === "text" && !f.text.trim())} data-testid="wm-save" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white">
            {saving ? <Loader2 size={16} className="animate-spin mr-1.5" /> : null} Save & apply to all photos
          </Button>
          <button onClick={load} className="text-xs text-slate-400 hover:text-[#F5551E] inline-flex items-center gap-1" data-testid="wm-refresh"><RefreshCw size={12} /> Refresh</button>
        </div>
        <Progress p={f.progress} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
        <h3 className="font-semibold text-[#12234A] text-sm mb-3">Preview</h3>
        {f.enabled ? (
          previewKey && <img src={`${API}/admin/site/watermark/preview?${previewKey}`} alt="Watermark preview" className="w-full max-h-[560px] object-contain bg-slate-50 rounded-xl border border-slate-100" data-testid="wm-preview" />
        ) : <div className="aspect-[3/2] rounded-xl bg-slate-50 flex items-center justify-center text-sm text-slate-400" data-testid="wm-preview-off">Watermark is off. Photos show without a mark.</div>}
      </div>
    </div>
  );
}
